import chalk from "chalk";
import { createContext, type PropsWithChildren, useEffect, useState } from "react";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import type { ConfigurationTabEnum } from "@/features/sub-families/types";
import useEffectOnce from "@/hooks/use-effect-once.ts";
import useInitializedContext from "@/hooks/use-initialized-context.ts";
import Logger from "@/lib/logger.ts";
import type { Locale, Navigation, Nullable, SiteEnum, WebComponentModeEnum } from "@/types";

type GlobalsContextType = {
    configurationTab: Nullable<ConfigurationTabEnum>;
    mode: WebComponentModeEnum;
    property: Nullable<string>;
    navigation: Nullable<Navigation>;
    site: SiteEnum;
    locale: Locale;
    apiTokenUrl: Nullable<string>;
    clearProperty: () => void;
};

const GlobalsContext = createContext<Nullable<GlobalsContextType>>(null);

export const useGlobals = () => useInitializedContext(GlobalsContext, "GlobalsProvider");

export type Globals = Pick<
    GlobalsContextType,
    "configurationTab" | "mode" | "property" | "navigation" | "site" | "locale" | "apiTokenUrl"
>;
type Props = PropsWithChildren<Globals>;
export default function GlobalsProvider({ children, ...data }: Props) {
    const [property, setProperty] = useState<Nullable<string>>(data.property);

    const clearProperty = () => {
        setProperty(null);
    };

    useEffectOnce(() => {
        DiagnosticsStore.collectGlobals({
            mode: data.mode,
            site: data.site,
            locale: data.locale,
            configurationTab: data.configurationTab,
            property: data.property,
            apiTokenUrl: data.apiTokenUrl,
        });
    });

    useEffect(() => {
        Logger.info("Loading component for location:", chalk.cyan(data.mode));
        DiagnosticsStore.collectEventLog("info", `Loading component for location: ${data.mode}`);
    }, [data.mode]);

    return <GlobalsContext value={{ ...data, property, clearProperty }}>{children}</GlobalsContext>;
}
